import profile from '@/config/profile';
import React, { useState, useEffect, useRef } from 'react';

const ContactSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const sectionRef = useRef(null);
  const isLightTheme = profile?.theme === 'light';

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    // Mock form submission
    alert('Message sent!');
    setFormData({ name: '', email: '', message: '' });
  };

  return (
    <section 
      id="contact"
      ref={sectionRef}
      className="min-h-screen flex items-center justify-center px-8 py-20"
    >
      <div className="max-w-6xl mx-auto w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left side - Contact info */}
          <div>
            <h2 className={`text-sm font-light tracking-widest mb-8 transition-all duration-1000 transform ${
              isLightTheme ? 'text-slate-600' : 'text-gray-400'
            } ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}>
              GET IN TOUCH
            </h2>
            
            <h3 className={`text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-8 transition-all duration-1000 delay-200 transform ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'
            }`}>
              Let's expand business{' '}
              <span className="text-blue-500">amazing</span> together.
            </h3>
            
            <p className={`text-lg mb-8 leading-relaxed transition-all duration-1000 delay-400 transform ${
              isLightTheme ? 'text-slate-600' : 'text-gray-300'
            } ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}>
              Connect with me.
            </p>
            
            <div className={`space-y-4 transition-all duration-1000 delay-600 transform ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}>
              <div className="flex items-center space-x-4 group cursor-pointer">
                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <span className="text-black font-bold">@</span>
                </div>
                <span className={`transition-colors duration-300 ${
                  isLightTheme ? 'text-slate-900 group-hover:text-blue-500' : 'text-white group-hover:text-blue-400'
                }`}>
                  {profile?.contact?.email || ''}
                </span>
              </div>
              
              <div className="flex items-center space-x-4 group cursor-pointer">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300 ${
                  isLightTheme ? 'bg-slate-200' : 'bg-gray-800'
                }`}>
                  <span className={`font-bold ${isLightTheme ? 'text-slate-900' : 'text-white'}`}>#</span>
                </div>
                <span className={`transition-colors duration-300 ${
                  isLightTheme ? 'text-slate-900 group-hover:text-blue-500' : 'text-white group-hover:text-blue-400'
                }`}>
                  {profile?.contact?.phone || ''}
                </span>
              </div>
            </div>
          </div>
          
          {/* Right side - Contact form */}
          <div className={`transition-all duration-1000 delay-800 transform ${
            isVisible ? 'translate-x-0 opacity-100' : 'translate-x-20 opacity-0'
          }`}>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Your Name"
                  required
                  className={`w-full px-0 py-4 bg-transparent border-0 border-b-2 focus:border-blue-500 focus:outline-none transition-colors duration-300 ${
                    isLightTheme ? 'border-slate-300 text-slate-900 placeholder-slate-500' : 'border-gray-700 text-white placeholder-gray-400'
                  }`}
                />
              </div>
              
              <div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Your Email"
                  required
                  className={`w-full px-0 py-4 bg-transparent border-0 border-b-2 focus:border-blue-500 focus:outline-none transition-colors duration-300 ${
                    isLightTheme ? 'border-slate-300 text-slate-900 placeholder-slate-500' : 'border-gray-700 text-white placeholder-gray-400'
                  }`}
                />
              </div>
              
              <div>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Your Message"
                  required
                  rows={4}
                  className={`w-full px-0 py-4 bg-transparent border-0 border-b-2 focus:border-blue-500 focus:outline-none resize-none transition-colors duration-300 ${
                    isLightTheme ? 'border-slate-300 text-slate-900 placeholder-slate-500' : 'border-gray-700 text-white placeholder-gray-400'
                  }`}
                />
              </div>
              
              <button
                type="submit"
                className="group relative overflow-hidden bg-blue-500 hover:bg-blue-600 text-black font-bold py-4 px-8 rounded-full transition-all duration-300 hover:scale-105 hover:shadow-xl"
              >
                <span className="relative z-10">Send Message</span>
                <div className="absolute inset-0 bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;